import has from 'lodash/has';
import isEqual from 'lodash/isEqual';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useDDPSubscription } from '@zedoc/ddp-connector';
import Pagination from '../common/components/Pagination';
import { default as CountSelect } from '../common/selectors/Count';
import {
  defaultPageSizeOptions,
  getNumberOfPages,
  getVisiblePage,
} from './paginationHelpers';

const constant = (x) => () => x;
const nothing = constant(null);

// NOTE: This is not really needed to look at currentPage here as subscriptionId already determines
//       current page index. Additionally, not having this additional dependency helps reducing
//       content flash right before the fetched results are displayed.
export const getItemsSelector = (selector, paginationKey) => {
  return selector
    .all()
    .satisfying((item) => has(item, paginationKey))
    .sort({
      [`${paginationKey}.index`]: 1,
    });
};

const usePagination = ({
  selector,
  mapSelector,
  getSubscription,
  resetPageKey,
  debounceMs = 250,
  pageSizeOptions = defaultPageSizeOptions,
}) => {
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(parseInt(pageSizeOptions[0], 10));
  // A page number only means something within one result set. A caller that
  // passes `resetPageKey` (its filters) gets the first page again whenever
  // that value changes: staying on, say, page 5 asked the server for a deep
  // page of a different - and after "Clear" unfiltered - result set, which is
  // both the wrong page to show and the most expensive request the list can
  // make. Decided during render and before getSubscription is called, so the
  // deep page of the new result set is never subscribed to and the callback
  // still runs once per render.
  //
  // Opt-in on purpose. Deriving this from the subscription params broke
  // callers that put more than the query in them (a `version` hash built from
  // the page itself) or that return no subscription for a while.
  const [prevResetPageKey, setPrevResetPageKey] = useState(resetPageKey);
  const shouldResetPage = !isEqual(resetPageKey, prevResetPageKey);
  if (shouldResetPage) {
    setPrevResetPageKey(resetPageKey);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }
  const subscription = getSubscription(
    shouldResetPage ? 1 : currentPage,
    pageSize,
  );
  const total = useSelector(
    subscription
      ? CountSelect.one().whereIdEquals(
          `${subscription.name}.${subscriptionId}`,
        )
      : nothing,
  );
  const { id, ready } = useDDPSubscription(subscription, {
    debounceMs,
  });

  useEffect(() => {
    if (id && ready) {
      setSubscriptionId(id);
    }
  }, [id, ready]);
  // True while the displayed items don't yet correspond to the requested
  // subscription: either the current request isn't ready, or the ready
  // request is a different one than the items on screen (subscriptionId is
  // only advanced once the new subscription becomes ready, so during a
  // page/filter change the list still shows the previous page). `ready`
  // alone can't be trusted here — a falsy request reports ready=true.
  const isFetching =
    !!subscription && (!ready || (id || null) !== subscriptionId);
  const paginationKey = `_pagination_${subscriptionId}`;
  const selectItems = useMemo(() => {
    if (!selector) {
      return constant([]);
    }
    const nextSelector = getItemsSelector(selector, paginationKey);
    if (mapSelector) {
      return mapSelector(nextSelector);
    }
    return nextSelector;
  }, [selector, mapSelector, paginationKey]);
  const items = useSelector(selectItems);
  const totalItems = total ? total.count : 0;
  const nPages = getNumberOfPages(pageSize, totalItems);
  const visiblePage = getVisiblePage(currentPage, nPages);
  useEffect(() => {
    if (visiblePage < currentPage && setCurrentPage) {
      setCurrentPage(visiblePage);
    }
  }, [visiblePage, currentPage, setCurrentPage]);
  const onPageSizeChange = useCallback(
    (_, value) => setPageSize(value),
    [setPageSize],
  );
  const getPaginationProps = () => ({
    total: totalItems,
    current: visiblePage,
    onChange: setCurrentPage,
    pageSize,
    pageSizeOptions,
    size: 'small',
    showSizeChanger: true,
    onShowSizeChange: onPageSizeChange,
  });
  const renderPagination = () => {
    const defaultProps = getPaginationProps();
    return (
      <Pagination
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...defaultProps}
      />
    );
  };
  return {
    ready,
    isFetching,
    items,
    nPages,
    totalItems,
    paginationKey,
    currentPage,
    pageSize,
    subscriptionId,
    setCurrentPage,
    renderPagination,
    getPaginationProps,
  };
};

export default usePagination;
